import { useId, useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { useT } from '../../i18n';
import { errorMessage } from '../../i18n/errorMessage';
import { lookupPlayersByName } from '../../lib/api/players';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// FIND A PLAYER FROM ANOTHER CLUB, BY FULL NAME (#1060).
//
// The club's player list is its own roster plus the players in its own events.
// A guest who has never played here is therefore not in it, and every picker
// draws on that list. This is the one way to reach them: the FULL name, exactly,
// and the answer is the name and the rating — never an address.
//
// «Ta med» puts the player in this session's list, so every picker can find
// them. They stay listed once they are in one of the club's events; until
// then, the next load does not show them again. The screen says so.
// ═══════════════════════════════════════════════════════════════════

export interface PlayerLookupPanelProps {
  /** Ids already in the club's list — those are shown as already there. */
  knownIds: ReadonlySet<string>;
  /** Put a found player into this session's player list. */
  onAdopt: (player: any) => void;
}

export function PlayerLookupPanel({ knownIds, onAdopt }: PlayerLookupPanelProps) {
  const t = useT();
  const uid = safeId('player-lookup', useId());
  const [name, setName] = useState('');
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'working' }
    | { kind: 'done'; players: any[] }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  const search = async () => {
    const asked = name.trim();
    if (asked.length < 3) {
      setState({ kind: 'failed', message: t('players.lookup.tooShort') });
      return;
    }
    setState({ kind: 'working' });
    const res = await lookupPlayersByName(asked);
    if (res.state === 'ok') setState({ kind: 'done', players: res.players });
    else if (res.state === 'refused') {
      setState({ kind: 'failed', message: errorMessage(t, res.reason) });
    } else setState({ kind: 'failed', message: t('players.lookup.unreachable') });
  };

  return (
    <section className="card p-4 mb-4" aria-labelledby={`${uid}-title`} data-testid="player-lookup">
      <h2 id={`${uid}-title`} className="font-semibold text-sm flex items-center gap-2">
        <Search size={14} aria-hidden="true" /> {t('players.lookup.title')}
      </h2>
      <p className="text-[12px] text-ink-3 mt-1">{t('players.lookup.explain')}</p>
      <form
        className="flex items-end gap-2 mt-3"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        <div className="flex-1">
          <label htmlFor={`${uid}-name`} className="block text-[11px] text-ink-3">
            {t('players.lookup.nameLabel')}
          </label>
          <input
            id={`${uid}-name`}
            className="input w-full"
            value={name}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
            data-testid="player-lookup-name"
          />
        </div>
        <button
          type="submit"
          className="btn-outline text-xs"
          disabled={state.kind === 'working'}
          data-testid="player-lookup-search"
        >
          {t('players.lookup.search')}
        </button>
      </form>

      {state.kind === 'working' && (
        <p className="text-sm text-ink-3 mt-3" role="status">
          {t('players.lookup.working')}
        </p>
      )}
      {state.kind === 'failed' && (
        <p className="text-sm text-rose-700 mt-3" role="alert" data-testid="player-lookup-failed">
          {state.message}
        </p>
      )}
      {state.kind === 'done' && state.players.length === 0 && (
        <p className="text-sm text-ink-3 mt-3" role="status" data-testid="player-lookup-none">
          {t('players.lookup.none')}
        </p>
      )}
      {state.kind === 'done' && state.players.length > 0 && (
        <ul className="mt-3 divide-y divide-line" data-testid="player-lookup-results">
          {state.players.map((p) => {
            const already = knownIds.has(String(p.id));
            return (
              <li key={String(p.id)} className="flex items-center justify-between gap-2 py-2">
                <span className="text-sm">
                  {p.name}{' '}
                  <span className="text-ink-3 text-[12px]">
                    {t('players.lookup.elo', { elo: Math.round(Number(p.elo) || 0) })}
                  </span>
                </span>
                {already ? (
                  <span className="text-[12px] text-ink-3">
                    {t('players.lookup.alreadyListed')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn-outline text-xs"
                    onClick={() => onAdopt(p)}
                    data-testid="player-lookup-adopt"
                  >
                    <UserPlus size={12} aria-hidden="true" /> {t('players.lookup.adopt')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
